import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
} from "@mui/material";
import { Edit, Delete, Download } from "@mui/icons-material"; // Import necessary icons
import { collection, query, where, onSnapshot, deleteDoc, doc, getDocs } from "firebase/firestore";
import { firestore as db } from "../../firebase"
import ChecklistForm from "./ChecklistForm"; // Import the ChecklistForm component
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const UserChecklist = ({ email }) => {
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false); // To control the form visibility
  const [selectedChecklist, setSelectedChecklist] = useState(null); // To store the checklist being edited
  const [isAdmin, setIsAdmin] = useState(false);
  
  // NEW: Add active only filter state
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!email) return;
      
      try {
        const userProfileQuery = query(
          collection(db, 'UserProfileMaster'),
          where('email', '==', email)
        );
        const userProfileSnapshot = await getDocs(userProfileQuery);
        
        if (!userProfileSnapshot.empty) {
          const userProfileData = userProfileSnapshot.docs[0].data();
          setIsAdmin(userProfileData.role === 'admin');
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    };

    checkAdminStatus();
  }, [email]);

  useEffect(() => {
    if (!email) return;

    setLoading(true);

    let q;
    if (isAdmin) {
      // For admin users, fetch all assigned checklists to see all user activities
      q = collection(db, "assignedChecklists");
    } else {
      // For regular users, fetch only their assigned checklists
      q = query(
        collection(db, "assignedChecklists"),
        where("assignedTo", "==", email)
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedChecklists = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        // ORIGINAL: Set all checklists without filtering
        // setChecklists(fetchedChecklists);
        
        // NEW: Apply active filter
        const filteredChecklists = showActiveOnly 
          ? fetchedChecklists.filter(checklist => checklist.IsActive === true)
          : fetchedChecklists;
        setChecklists(filteredChecklists);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching assigned checklists:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [email, isAdmin, showActiveOnly]); // NEW: Add showActiveOnly as dependency

  const openForm = (checklist) => {
    setSelectedChecklist(checklist); // Set the selected checklist
    setIsFormOpen(true); // Open the form dialog
  };

  const closeForm = () => {
    setIsFormOpen(false); // Close the form dialog
    setSelectedChecklist(null); // Reset the selected checklist
  };

  const handleDeleteChecklist = async (id) => {
    await deleteDoc(doc(db, "assignedChecklists", id)); // Delete the checklist
  };

  const generatePDF = (checklist) => {
    const doc = new jsPDF();

    const headers = checklist.columns.map((col) => col.name);
    const rows = checklist.rows.map((row) =>
      checklist.columns.map((col) => row.data[col.id] || "")
    );

    doc.text(checklist.name, 10, 10);
    autoTable(doc, {
      head: [headers],
      body: rows,
      startY: 20,
      theme: "grid",
    });

    let yOffset = doc.lastAutoTable.finalY + 10;

    if (checklist.remarks) {
      doc.text(`Remarks: ${checklist.remarks}`, 10, yOffset);
      yOffset += 10;
    }

    if (checklist.images && checklist.images.length > 0) {
      checklist.images.forEach((image) => {
        doc.addImage(image, "JPEG", 10, yOffset, 50, 50);
        yOffset += 55;
      });
    }

    const fileName = checklist.name ? `${checklist.name}.pdf` : "Checklist.pdf";
    doc.save(fileName);
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ my: 2 }}>
        {isAdmin ? "All Assigned Checklists" : "User Assigned Checklists"}
      </Typography>

      {/* Display the passed email */}
      <Typography variant="body1" sx={{ my: 1 }}>
        {isAdmin ? "Viewing all assigned checklists" : `Viewing checklists for: <strong>${email}</strong>`}
      </Typography>
      
      {/* NEW: Active only filter checkbox */}
      <div style={{ marginBottom: "16px", display: "flex", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
          <input 
            type="checkbox" 
            checked={showActiveOnly} 
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            style={{ marginRight: "4px" }}
          />
          Active only
        </label>
      </div>

      {/* Loading indicator */}
      {loading && (
        <Container>
          <CircularProgress />
        </Container>
      )}

      {/* Checklist display */}
      {!loading && checklists.length > 0 ? (
        <List>
          {checklists.map((checklist) => (
            <ListItem key={checklist.id} sx={{ display: "flex", justifyContent: "space-between" }}>
              <ListItemText
                primary={checklist.name}
                secondary={checklist.description}
              />
              <div>
                {/* Edit Icon */}
                <Tooltip title="Edit">
                  <IconButton
                    color="primary"
                    onClick={() => openForm(checklist)}
                    sx={{ marginRight: "10px" }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>

                {/* Delete Icon */}
                <Tooltip title="Delete">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteChecklist(checklist.id)}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>

                {/* Download Icon */}
                <Tooltip title="Download">
                  <IconButton
                    color="secondary"
                    onClick={() => generatePDF(checklist)}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
              </div>
            </ListItem>
          ))}
        </List>
      ) : (
        !loading && (
          <Typography variant="body1">No assigned checklists found.</Typography>
        )
      )}

      {/* Checklist Form Dialog */}
      {isFormOpen && (
        <Dialog open={isFormOpen} onClose={closeForm} maxWidth="lg" fullWidth>
          <DialogTitle>Edit Checklist</DialogTitle>
          <DialogContent>
            <ChecklistForm
              selectedChecklist={selectedChecklist} // Pass the selected checklist to the form
              onClose={closeForm} // Close form handler
            />
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default UserChecklist;
